'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, BookOpen, Layers, Play, Map, User } from 'lucide-react';
import { XPBar } from '@/components/gamification/XPBar';
import { StreakDisplay } from '@/components/gamification/StreakDisplay';
import { MobileMenu } from './MobileMenu';

const NAV_LINKS = [
  { href: '/questions', label: 'Questions', icon: BookOpen },
  { href: '/categories', label: 'Categories', icon: Layers },
  { href: '/quiz', label: 'Quiz', icon: Play },
  { href: '/roadmap', label: 'Roadmap', icon: Map },
  { href: '/profile', label: 'Profile', icon: User },
];

export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-40 glass"
        style={{ height: '64px' }}
      >
        <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-4 md:px-8">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <span
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-sm"
              style={{ background: 'var(--color-primary)' }}
            >
              D
            </span>
            <span
              className="font-display font-bold text-lg hidden sm:block"
              style={{ color: 'var(--color-text-primary)' }}
            >
              dart<span className="gradient-text">dosage</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {NAV_LINKS.map(({ href, label }) => {
              const active = pathname.startsWith(href);
              return (
                <Link
                  key={href}
                  href={href}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                  style={{
                    color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                    background: active ? 'rgba(91,79,232,0.12)' : 'transparent',
                  }}
                >
                  {label}
                </Link>
              );
            })}
          </nav>

          {/* Right: XP + streak + CTA */}
          <div className="flex items-center gap-3">
            {/* XP bar — desktop only */}
            <div className="hidden lg:block w-48">
              <XPBar compact />
            </div>

            {/* Streak */}
            <div className="hidden sm:block">
              <StreakDisplay size="sm" />
            </div>

            {/* Quiz CTA */}
            <Link
              href="/quiz"
              className="hidden md:flex btn-primary py-2 px-4 text-sm"
            >
              <Play size={14} />
              Quiz
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-secondary)' }}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
